import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { salesAPI } from '../services/api';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

/** Months from Jan (current year) to current month */
function monthChoicesFromJan() {
  const out = [];
  const d = new Date();
  for (let m = 0; m <= d.getMonth(); m += 1) {
    const x = new Date(d.getFullYear(), m, 1);
    const key = `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`;
    const label = x.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    out.push({ key, label });
  }
  return out.reverse(); // newest first in dropdown
}

function currentMonthKey() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

export default function MySales() {
  const months = useMemo(() => monthChoicesFromJan(), []);
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!months.length) return;
    if (!months.some((m) => m.key === monthKey)) {
      setMonthKey(months[0].key);
    }
  }, [months, monthKey]);
  const [sales, setSales] = useState([]);
  const [totalAmount, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await salesAPI.list({ month: monthKey });
        if (cancelled) return;
        setSales(res.data.sales || []);
        setTotal(res.data.totalAmount || 0);
      } catch {
        if (!cancelled) {
          setSales([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [monthKey, refreshTick]);

  const selectedLabel = months.find((m) => m.key === monthKey)?.label || monthKey;

  const isEmiSale = (s) => s.paymentMode === 'EMI' || s.isEmi || s.saleType === 'emi';
  const cashCount = sales.filter((s) => !isEmiSale(s)).length;
  const emiCount = sales.filter((s) => isEmiSale(s)).length;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="mb-0 fw-700">My Sales</h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Branch sales — month list: January to current month
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <select
            className="form-select form-select-sm"
            style={{ minWidth: 200 }}
            value={monthKey}
            aria-label="Select month"
            onChange={(e) => setMonthKey(e.target.value)}
          >
            {months.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            title="Refresh"
            onClick={() => setRefreshTick((t) => t + 1)}
          >
            ↻
          </button>
        </div>
      </div>

      <div className="text-muted small mb-3">
        Showing: <strong className="text-dark">{selectedLabel}</strong>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #0d6efd' }}>
            <div className="stat-label">Total sales</div>
            <div className="stat-value" style={{ color: '#0d6efd' }}>{sales.length}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #198754' }}>
            <div className="stat-label">Revenue</div>
            <div className="stat-value" style={{ color: '#198754', fontSize: 20 }}>{fmt(totalAmount)}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #0891b2' }}>
            <div className="stat-label">Cash sales</div>
            <div className="stat-value" style={{ color: '#0891b2' }}>{cashCount}</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #7c3aed' }}>
            <div className="stat-label">EMI sales</div>
            <div className="stat-value" style={{ color: '#7c3aed' }}>{emiCount}</div>
          </div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Date &amp; Time</th>
                <th>Product</th>
                <th>Customer</th>
                <th>Mode</th>
                <th className="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="5" className="text-center py-4">Loading…</td></tr>
              )}
              {!loading && sales.length === 0 && (
                <tr>
                  <td colSpan="5">
                    <div className="empty-state">
                      <div className="es-icon">🛒</div>
                      <div className="es-title">No sales found</div>
                      <div>No sales in {selectedLabel}.</div>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && sales.map((s) => (
                <tr key={s._id}>
                  <td style={{ fontSize: 12 }}>{new Date(s.date).toLocaleString('en-IN')}</td>
                  <td>
                    <strong style={{ fontSize: 13 }}>{s.productName}</strong>
                    {s.category && <div className="text-muted" style={{ fontSize: 11 }}>{s.category}</div>}
                  </td>
                  <td>
                    {s.linkedCustomerId ? (
                      <Link to={`/customers/${s.linkedCustomerId}`} className="fw-700" style={{ fontSize: 13 }}>
                        {s.customerName || '—'}
                      </Link>
                    ) : (
                      <span style={{ fontSize: 13 }}>{s.customerName || '—'}</span>
                    )}
                    {s.customerPhone && <div className="text-muted" style={{ fontSize: 11 }}>{s.customerPhone}</div>}
                    {isEmiSale(s) && s.witnessName && (
                      <div className="text-muted" style={{ fontSize: 11 }}>Witness: {s.witnessName}</div>
                    )}
                  </td>
                  <td>
                    <span className={`chip ${isEmiSale(s) ? 'badge-emi' : 'badge-cash'}`}>
                      {isEmiSale(s) ? 'EMI' : 'Cash'}
                    </span>
                    {isEmiSale(s) && s.downPayment > 0 && (
                      <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                        DP: {fmt(s.downPayment)} + {s.emiMonths || '?'}× kist
                      </div>
                    )}
                  </td>
                  <td className="text-end"><strong>{fmt(s.salePrice)}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
